import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, ConfigProvider, theme as antdTheme, Checkbox, Input as AntdInput, Select, Table, TableProps } from "antd";
import { getProdutoById, saveProduto } from "@/services/produto";
import { getUnidades } from "@/services/unidade";
import { getAllEspecificas } from "@/services/especifica";
import { getAllCategoriasProduto } from "@/services/categoriaProduto";
import { errorHandler } from "@/utils/errorhandler";

const defaultValues = {
  nome: "",
  descricao: "",
  ativo: true,
  categoriaProdutoId: undefined as number | undefined,
};

function formatCurrency(value: string | number) {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "number" ? value : Number(value.toString().replace(/\D/g, "")) / 100;
  return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

type LinhaUnidade = {
  unidadeId: number;
  unidadeNome: string;
  valor: string;
  valorVinculado: string;
  quantidadeEstoque: string;
  /** Movimentada só pelas vendas; a tela apenas exibe. */
  quantidadeReservada: number;
};

export default function CadastroProduto() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [linhasUnidades, setLinhasUnidades] = useState<LinhaUnidade[]>([]);
  const [especificas, setEspecificas] = useState<any[]>([]);
  const [especificasIds, setEspecificasIds] = useState<number[]>([]);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [erroCategoria, setErroCategoria] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    const carregar = async () => {
      try {
        const [unidadesResponse, especificasResponse, categoriasResponse] = await Promise.all([
          getUnidades(),
          getAllEspecificas(),
          getAllCategoriasProduto({ somenteAtivas: true }),
        ]);
        const categoriasAtivas = categoriasResponse.data || [];
        const unidades = (unidadesResponse.data || []).filter((u: any) => u.ativo ?? true);
        setEspecificas(
          (especificasResponse.data || []).filter((e: any) => e.ativo ?? true)
        );

        let produto: any = null;
        if (isEdit && id) {
          const produtoResponse = await getProdutoById(id);
          if (produtoResponse.status === 200) {
            produto = produtoResponse.data;
            reset({
              ...defaultValues,
              nome: produto.nome ?? "",
              descricao: produto.descricao ?? "",
              ativo: produto.ativo !== undefined ? produto.ativo : true,
              categoriaProdutoId: produto.categoriaProdutoId,
            });
            setEspecificasIds(
              (produto.especificas || []).map((e: any) => e.especificaId)
            );
          }
        } else {
          reset(defaultValues);
          setEspecificasIds([]);
        }

        // Categoria inativa de um produto já cadastrado continua visível no select
        // para não sumir silenciosamente da tela de edição.
        const categoriaAtual = produto?.categoriaProdutoId;
        setCategorias(
          categoriaAtual && !categoriasAtivas.some((c: any) => c.id === categoriaAtual)
            ? [...categoriasAtivas, { id: categoriaAtual, nome: produto.categoriaProdutoNome }]
            : categoriasAtivas
        );

        // Toda unidade ativa ganha uma linha; produto existente preenche as suas.
        setLinhasUnidades(
          unidades.map((u: any) => {
            const configurada = produto?.unidades?.find((pu: any) => pu.unidadeId === u.id);
            return {
              unidadeId: u.id,
              unidadeNome: u.nome,
              valor: formatCurrency(configurada?.valor ?? 0),
              valorVinculado: formatCurrency(configurada?.valorVinculado ?? 0),
              quantidadeEstoque: String(configurada?.quantidadeEstoque ?? 0),
              quantidadeReservada: Number(configurada?.quantidadeReservada ?? 0),
            };
          })
        );
      } catch (error) {
        errorHandler(error);
      }
    };
    carregar();
  }, [id, isEdit, reset]);

  // A coluna "Valor Vinculado" só abre quando existe específica vinculada.
  const possuiVinculo = especificasIds.length > 0;

  const atualizarLinha = (
    unidadeId: number,
    campo: "valor" | "valorVinculado" | "quantidadeEstoque",
    valor: string
  ) => {
    setLinhasUnidades((prev) =>
      prev.map((linha) => (linha.unidadeId === unidadeId ? { ...linha, [campo]: valor } : linha))
    );
  };

  const onSubmit = async (data: any) => {
    // O select do antd é controlado por setValue e o reset() do react-hook-form
    // descarta regras registradas na mão, então a obrigatoriedade fica aqui.
    if (!data.categoriaProdutoId) {
      setErroCategoria("Categoria é obrigatória");
      return;
    }
    setErroCategoria(null);
    try {
      const payload = {
        id: id || undefined,
        nome: data.nome,
        descricao: data.descricao || null,
        ativo: !!data.ativo,
        categoriaProdutoId: data.categoriaProdutoId,
        unidades: linhasUnidades.map((linha) => ({
          unidadeId: linha.unidadeId,
          valor: parseCurrency(linha.valor),
          // Sem específica vinculada não há venda por vínculo: o valor não é editável
          // e vai zerado, para não guardar preço de uma regra que não se aplica.
          valorVinculado: possuiVinculo ? parseCurrency(linha.valorVinculado) : 0,
          quantidadeEstoque: Number(linha.quantidadeEstoque) || 0,
        })),
        especificasIds,
      };
      const response = await saveProduto(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Produto salvo com sucesso!");
        navigate("/produto");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const colunasUnidades: TableProps<LinhaUnidade>["columns"] = [
    {
      title: "Unidade",
      dataIndex: "unidadeNome",
      key: "unidadeNome",
    },
    {
      title: "Valor Avulso",
      key: "valor",
      width: 180,
      render: (_, linha) => (
        <Input
          type="text"
          placeholder="R$ 0,00"
          aria-label={`Valor Avulso - ${linha.unidadeNome}`}
          value={linha.valor}
          onChange={(e) => {
            let value = e.target.value.replace(/\D/g, "");
            if (value.length < 3) value = value.padStart(3, "0");
            atualizarLinha(linha.unidadeId, "valor", formatCurrency(Number(value) / 100));
          }}
        />
      ),
    },
    {
      title: "Valor Vinculado",
      key: "valorVinculado",
      width: 180,
      // Só faz sentido quando o produto tem específica vinculada: é o preço
      // cobrado quando ele entra sozinho na matrícula, junto da específica.
      render: (_, linha) => (
        <Input
          type="text"
          placeholder="R$ 0,00"
          aria-label={`Valor Vinculado - ${linha.unidadeNome}`}
          disabled={!possuiVinculo}
          title={
            possuiVinculo
              ? undefined
              : "Selecione uma específica vinculada para informar o valor"
          }
          value={possuiVinculo ? linha.valorVinculado : ""}
          onChange={(e) => {
            let value = e.target.value.replace(/\D/g, "");
            if (value.length < 3) value = value.padStart(3, "0");
            atualizarLinha(linha.unidadeId, "valorVinculado", formatCurrency(Number(value) / 100));
          }}
        />
      ),
    },
    {
      title: "Quantidade Estoque",
      key: "quantidadeEstoque",
      width: 180,
      render: (_, linha) => (
        <Input
          type="number"
          min={0}
          step={1}
          placeholder="0"
          aria-label={`Estoque - ${linha.unidadeNome}`}
          value={linha.quantidadeEstoque}
          onChange={(e) => atualizarLinha(linha.unidadeId, "quantidadeEstoque", e.target.value)}
        />
      ),
    },
    {
      title: "Quantidade Reservada",
      key: "quantidadeReservada",
      width: 180,
      // Somente leitura: quem move este número é a venda (matrícula ou avulsa),
      // que tira do estoque e põe aqui até a entrega.
      render: (_, linha) => (
        <span aria-label={`Reservada - ${linha.unidadeNome}`}>
          {linha.quantidadeReservada.toLocaleString("pt-BR")}
        </span>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Produto" : "Cadastro de Produto"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center mt-6 col-span-2">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="ativo"
                checked={watch("ativo")}
                onChange={(e) => setValue("ativo", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="ativo" className="text-white ml-2">Ativo</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Nome *</label>
            <Input
              type="text"
              placeholder="Nome"
              maxLength={100}
              {...register("nome", {
                required: "Nome é obrigatório",
                maxLength: { value: 100, message: "O nome deve ter no máximo 100 caracteres" },
              })}
            />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Descrição</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <AntdInput.TextArea
                placeholder="Descrição do produto"
                rows={4}
                maxLength={4000}
                showCount
                value={watch("descricao")}
                onChange={(e) => setValue("descricao", e.target.value, { shouldValidate: true })}
              />
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Categoria *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                className="w-full"
                placeholder="Selecione a categoria do produto"
                aria-label="Categoria"
                value={watch("categoriaProdutoId")}
                onChange={(value) => {
                  setValue("categoriaProdutoId", value, { shouldValidate: true });
                  setErroCategoria(null);
                }}
                options={categorias.map((c) => ({ value: c.id, label: c.nome }))}
              />
            </ConfigProvider>
            {erroCategoria && <span className="text-red-600">{erroCategoria}</span>}
          </div>
          <div className="col-span-2">
            <label>Específicas vinculadas</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Selecione as específicas vinculadas ao produto"
                value={especificasIds}
                onChange={setEspecificasIds}
                options={especificas.map((e) => ({ value: e.id, label: e.nome }))}
              />
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Registro por Unidade</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Table
                columns={colunasUnidades}
                dataSource={linhasUnidades}
                rowKey="unidadeId"
                pagination={false}
                size="small"
              />
            </ConfigProvider>
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/produto")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}
